import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Pause, Play, Rewind } from 'lucide-react';
import { CategoryIcon, ThreatBadge } from './Badges';
import { formatClock, timeAgo } from '../lib/api';
import { type InterfaceLanguage, uiText } from '../lib/i18n';
import type { EventCluster } from '../types';
import { englishOnlyText } from '../lib/englishOnly.mjs';

interface Props {
  language: InterfaceLanguage;
  events: EventCluster[];
  selectedEventId?: string;
  onSelect: (event: EventCluster) => void;
}

export default function TimelinePanel({ language, events, selectedEventId, onSelect }: Props) {
  const t = (text: string) => uiText(language, text);
  const prefersReducedMotion = useReducedMotion();
  const dateLanguage = language === 'hi' ? 'hi' : 'en';
  const timelineEvents = useMemo(() => events
    .filter((event) => Number.isFinite(Date.parse(event.latestAt)))
    .sort((left, right) => Date.parse(left.latestAt) - Date.parse(right.latestAt)), [events]);
  const [cursorIndex, setCursorIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  const selectedIndex = timelineEvents.findIndex((event) => event.id === selectedEventId);
  const requestedIndex = selectedIndex >= 0 ? selectedIndex : cursorIndex >= 0 ? cursorIndex : timelineEvents.length - 1;
  const activeIndex = timelineEvents.length ? Math.max(0, Math.min(requestedIndex, timelineEvents.length - 1)) : 0;
  const activeEvent = timelineEvents[activeIndex];

  useEffect(() => {
    if (cursorIndex >= timelineEvents.length) setCursorIndex(timelineEvents.length - 1);
    if (timelineEvents.length < 2) setPlaying(false);
  }, [cursorIndex, timelineEvents.length]);

  useEffect(() => {
    if (!playing || timelineEvents.length < 2) return;
    const timer = window.setInterval(() => {
      const nextIndex = (activeIndex + 1) % timelineEvents.length;
      setCursorIndex(nextIndex);
      onSelectRef.current(timelineEvents[nextIndex]);
    }, 1800);
    return () => window.clearInterval(timer);
  }, [playing, activeIndex, timelineEvents]);

  const selectIndex = (index: number) => {
    const event = timelineEvents[index];
    if (!event) return;
    setCursorIndex(index);
    onSelect(event);
  };

  return (
    <motion.section className="timeline-panel" aria-label={t('घटना समयरेखा')} initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: prefersReducedMotion ? 0 : 0.3 }}>
      <div className="timeline-heading">
        <div><span className="section-code">05 / {t('घटना समयरेखा')}</span><h2>{t('समयरेखा')}</h2></div>
        <span className="timeline-count">{timelineEvents.length} {t('घटनाएँ')}</span>
      </div>
      {activeEvent ? <>
        <div className="timeline-control-row">
          <button className="timeline-play" type="button" onClick={() => setPlaying((value) => !value)} disabled={timelineEvents.length < 2} aria-label={t(playing ? 'समयरेखा रोकें' : 'समयरेखा चलाएँ')} title={t(playing ? 'समयरेखा रोकें' : 'समयरेखा चलाएँ')}>
            {playing ? <Pause size={13} /> : <Play size={13} />}{t(playing ? 'रोकें' : 'चलाएँ')}
          </button>
          <div className="timeline-range-wrap">
            <label className="sr-only" htmlFor="event-timeline-range">{t('समयरेखा स्थिति')}</label>
            <input id="event-timeline-range" type="range" min={0} max={Math.max(0, timelineEvents.length - 1)} value={activeIndex} onChange={(event) => selectIndex(Number(event.target.value))} disabled={timelineEvents.length < 2} aria-valuetext={`${activeEvent.title}, ${formatClock(activeEvent.latestAt, dateLanguage)}`} />
            <div className="timeline-ends"><span>{t('सबसे पुरानी')} · {timeAgo(timelineEvents[0].latestAt, dateLanguage)}</span><span>{t('नवीनतम')} · {timeAgo(timelineEvents[timelineEvents.length - 1].latestAt, dateLanguage)}</span></div>
          </div>
          <button className="timeline-reset" type="button" onClick={() => selectIndex(timelineEvents.length - 1)} title={t('नवीनतम')} aria-label={t('नवीनतम')}><Rewind size={13} /></button>
        </div>
        <button className="timeline-active-event" type="button" onClick={() => onSelect(activeEvent)} aria-live="polite">
          <span className="event-marker"><CategoryIcon category={activeEvent.category} size={13} /></span>
          <span className="timeline-active-copy"><strong>{englishOnlyText(activeEvent.title, 'Non-English event headline hidden')}</strong><small>{englishOnlyText(activeEvent.place || t('स्थान पहचाना नहीं'), 'Location hidden')} · {activeEvent.reportCount} {t('रिपोर्ट')} · {activeEvent.sourceCount} {t('स्रोत')}</small></span>
          <ThreatBadge level={activeEvent.threatLevel} language={language} />
          <time>{formatClock(activeEvent.latestAt, dateLanguage)}</time>
          <span className="timeline-position">{activeIndex + 1}/{timelineEvents.length}</span>
        </button>
      </> : <div className="timeline-empty">{t('समयरेखा के लिए अभी कोई घटना नहीं')}</div>}
    </motion.section>
  );
}
